// ============================================================
//  ALTIMO Medical Center — Inventario.jsx  (v2 — Fase 1)
//  Ubicación: src/components/Inventario.jsx
//  Funcionalidades:
//    ✓ Lista de productos con estado visual de stock
//    ✓ Entrada de stock con número de lote, costo, vencimiento
//    ✓ Costo promedio ponderado (calculado en backend)
//    ✓ Vista de lotes por producto (trazabilidad)
//    ✓ Kardex completo con filtros
//    ✓ Ajuste manual (alta / baja) con razón
//    ✓ Crear / editar producto con campos nuevos
//    ✓ Medicamentos controlados marcados visualmente
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import TooltipAyuda from './TooltipAyuda';

// ── Paleta y estilos ─────────────────────────────────────────
const C = {
  azul:      '#1B2A5E',
  verde:     '#1D9E75',
  rojo:      '#dc3545',
  naranja:   '#e67e22',
  gris:      '#6c757d',
  amarillo:  '#856404',
  morado:    '#6f42c1',
};

const CAT_COLOR = {
  Antibiótico:    { bg:'#fde8e8', color:'#a02020' },
  AINE:           { bg:'#fff3cd', color:'#856404' },
  Gastroprotector:{ bg:'#d1ecf1', color:'#0c5460' },
  Solución:       { bg:'#cce5ff', color:'#004085' },
  Analgésico:     { bg:'#f4c0d1', color:'#72243E' },
  Insumo:         { bg:'#e2e3e5', color:'#383d41' },
  Otro:           { bg:'#e8f0fb', color:'#1B2A5E' },
};

const S = {
  pagina:    { background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:      { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'16px' },
  cardTit:   { fontSize:'12px', fontWeight:'700', color:C.azul, textTransform:'uppercase', letterSpacing:'.05em',
               marginBottom:'14px', paddingBottom:'10px', borderBottom:`2px solid #e8f0fb`,
               display:'flex', justifyContent:'space-between', alignItems:'center' },
  tabla:     { width:'100%', borderCollapse:'collapse', fontSize:'13px' },
  th:        { textAlign:'left', padding:'8px 10px', fontSize:'11px', color:C.gris, fontWeight:'600',
               borderBottom:'2px solid #e8f0fb', background:'#f8fafd', whiteSpace:'nowrap' },
  td:        { padding:'9px 10px', borderBottom:'1px solid #eef1f6', color:'#333', verticalAlign:'middle' },
  input:     { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda',
               borderRadius:'7px', background:'#fff', color:'#333', outline:'none', boxSizing:'border-box' },
  label:     { display:'block', fontSize:'11px', fontWeight:'600', color:C.gris,
               marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  campo:     { marginBottom:'12px' },
  fila2:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' },
  fila3:     { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' },
  btnVerde:  { background:C.verde, color:'#fff', border:'none', borderRadius:'7px',
               padding:'8px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnAzul:   { background:C.azul, color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnGris:   { background:'#fff', color:'#495057', border:'1px solid #c0ccda',
               borderRadius:'7px', padding:'7px 14px', fontSize:'12px', cursor:'pointer' },
  btnNaranja:{ background:C.naranja, color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnRojo:   { background:'#dc3545', color:'#fff', border:'none', borderRadius:'7px',
               padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnSm: (color) => ({
    background: color || '#e8f0fb', color: color ? '#fff' : C.azul,
    border:'none', borderRadius:'5px', padding:'4px 9px', fontSize:'11px',
    fontWeight:'600', cursor:'pointer', whiteSpace:'nowrap',
  }),
  alerta: (tipo) => ({
    padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'500', marginBottom:'12px',
    background: tipo==='ok'?'#d4edda': tipo==='err'?'#f8d7da':'#fff3cd',
    color:      tipo==='ok'?'#155724': tipo==='err'?'#721c24':'#856404',
    border:     `1px solid ${tipo==='ok'?'#c3e6cb': tipo==='err'?'#f5c6cb':'#ffc107'}`,
  }),
  modal:     { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,.45)',
               display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, overflowY:'auto' },
  modalCard: (w) => ({ background:'#fff', borderRadius:'12px', padding:'28px', width: w||'480px',
                       border:'1px solid #dde3ea', maxHeight:'90vh', overflowY:'auto', margin:'auto' }),
  pill: (cat) => ({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: CAT_COLOR[cat]?.bg || CAT_COLOR.Otro.bg,
    color:      CAT_COLOR[cat]?.color || CAT_COLOR.Otro.color,
  }),
  stockBar: (pct, color) => ({
    height:'6px', borderRadius:'99px', background: color,
    width:`${Math.min(Math.max(pct,2),100)}%`, transition:'width .3s',
  }),
  badge: (color) => ({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: color+'22', color, border:`1px solid ${color}44`,
  }),
  tabBtn: (activo) => ({
    padding:'8px 18px', borderRadius:'7px', fontSize:'13px', fontWeight:'600',
    cursor:'pointer', border:'none',
    background: activo ? C.azul : '#e8f0fb',
    color:      activo ? '#fff' : C.azul,
  }),
  // ── Añadidos para sub-pestaña "Reportes" (Inventario/Kardex/Controlados) ──
  totalBox: { background:C.azul, borderRadius:'10px', padding:'14px 20px',
              display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'10px' },
  btnNar:   { background:C.naranja, color:'#fff', border:'none', borderRadius:'7px',
              padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnMor:   { background:C.morado, color:'#fff', border:'none', borderRadius:'7px',
              padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
};

// ── Helpers ──────────────────────────────────────────────────
const hoy   = () => new Date().toISOString().slice(0,10);
const fmtFecha = (f) => f ? new Date(f+'T00:00:00').toLocaleDateString('es-SV') : '—';
const fmtMonto = (n) => `$${Number(n||0).toFixed(2)}`;
const diasHasta = (f) => {
  if (!f) return null;
  return Math.round((new Date(f+'T00:00:00') - new Date()) / (1000*60*60*24));
};

function colorStock(stock, minimo) {
  if (stock <= 0)       return C.rojo;
  if (stock <= minimo)  return C.rojo;
  if (stock <= minimo * 1.5) return C.naranja;
  return C.verde;
}

function colorVencimiento(fecha) {
  const d = diasHasta(fecha);
  if (d === null) return null;
  if (d <  0)  return C.rojo;
  if (d <= 7)  return C.rojo;
  if (d <= 30) return C.naranja;
  return C.verde;
}

// ── Helpers añadidos para sub-pestaña "Reportes" ─────────────
const fmtDT = (f) => f ? new Date(f).toLocaleString('es-SV') : '—';
const primerDiaMes = () => {
  const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
};

/** Exporta array de objetos a CSV y descarga */
function exportarCSV(datos, columnas, nombreArchivo) {
  const enc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const cabecera = columnas.map(c => enc(c.label)).join(',');
  const filas    = datos.map(row =>
    columnas.map(c => enc(row[c.key])).join(',')
  );
  const csv = [cabecera, ...filas].join('\n');
  const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${nombreArchivo}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/** Imprime HTML en ventana nueva → PDF del sistema */
function imprimirHTML(html, titulo) {
  const w = window.open('','_blank','width=900,height=700');
  w.document.write(`<!DOCTYPE html><html lang="es"><head>
    <meta charset="UTF-8"/>
    <title>${titulo}</title>
    <style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:Segoe UI,sans-serif; padding:28px 32px; color:#222; font-size:12px; }
      h1 { color:#1B2A5E; font-size:17px; margin-bottom:4px; }
      .sub { color:#6c757d; font-size:11px; margin-bottom:16px; }
      .linea { height:2px; background:linear-gradient(90deg,#1B2A5E,#1D9E75); margin-bottom:16px; }
      table { width:100%; border-collapse:collapse; font-size:11px; }
      th { background:#1B2A5E; color:#fff; padding:7px 8px; text-align:left; font-weight:600; }
      td { padding:6px 8px; border-bottom:1px solid #eee; }
      tr:nth-child(even) td { background:#f8fafd; }
      .total-box { background:#1B2A5E; color:#fff; padding:10px 16px; margin-top:12px;
                   border-radius:6px; display:flex; justify-content:space-between; }
      .total-box span:last-child { font-size:16px; font-weight:800; }
      .badge-ctrl { background:#f3e5f5; color:#6a1b9a; border:1px solid #ce93d8;
                    padding:1px 6px; border-radius:99px; font-size:9px; font-weight:700; margin-left:4px; }
      @media print { body { padding:12px; } }
    </style>
  </head><body>
    <h1>ALTIMO Medical Center</h1>
    <div class="sub">Generado: ${new Date().toLocaleString('es-SV')}</div>
    <div class="linea"></div>
    ${html}
    <script>window.onload=()=>{ setTimeout(()=>window.print(),400); }<\/script>
  </body></html>`);
  w.document.close();
}

const CATEGORIAS = ['Antibiótico','AINE','Gastroprotector','Analgésico','Solución','Insumo','Otro'];

// ── Formulario vacío producto ────────────────────────────────
const PROD_VACIO = {
  nombre:'', codigo:'', unidad:'', unidad_despacho:'',
  factor_conversion:1, stock_minimo:5, precio_unitario:0,
  es_controlado:false, categoria:'',
};

// ── Formulario vacío entrada ─────────────────────────────────
const ENT_VACIO = {
  numero_lote:'', cantidad:'', costo_unitario:'',
  fecha_vencimiento:'', proveedor:'', factura_proveedor:'', observacion:'',
};

// ── Formulario vacío ajuste ──────────────────────────────────
const AJU_VACIO = {
  tipo_ajuste:'Ajuste_Alta', lote_id:'', cantidad:'', motivo_ajuste:'',
};

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Inventario({ usuario }) {
  const [vista,        setVista]        = useState('productos');  // 'productos' | 'kardex'
  const [productos,    setProductos]    = useState([]);
  const [busqueda,     setBusqueda]     = useState('');
  const [filtroCat,    setFiltroCat]    = useState('Todos');
  const [filtroAlert,  setFiltroAlert]  = useState('Todos');    // Todos | StockBajo | PorVencer | Controlado
  const [mensaje,      setMensaje]      = useState(null);
  const [cargando,     setCargando]     = useState(false);

  // Modales
  const [modalProducto, setModalProducto] = useState(null);  // null | 'nuevo' | producto
  const [modalEntrada,  setModalEntrada]  = useState(null);  // null | producto
  const [modalLotes,    setModalLotes]    = useState(null);  // null | { producto, lotes }
  const [modalKardex,   setModalKardex]   = useState(null);  // null | { producto, movimientos }
  const [modalAjuste,   setModalAjuste]   = useState(null);  // null | producto

  // Forms
  const [formProd,   setFormProd]   = useState(PROD_VACIO);
  const [formEnt,    setFormEnt]    = useState(ENT_VACIO);
  const [formAjuste, setFormAjuste] = useState(AJU_VACIO);
  const [guardando,  setGuardando]  = useState(false);

  // Kardex filters
  const [kardexDesde, setKardexDesde] = useState('');
  const [kardexHasta, setKardexHasta] = useState('');

  const aviso = (tipo, texto, ms=3500) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje(null), ms);
  };

  // ── Carga de productos ──────────────────────────────────────
  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await api.inventario.listar();
    if (r.ok) setProductos(r.data);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Filtrado ────────────────────────────────────────────────
  const filtrados = productos.filter(p => {
    const matchBus = !busqueda ||
      p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      (p.codigo||'').toLowerCase().includes(busqueda.toLowerCase());
    const matchCat  = filtroCat === 'Todos' || p.categoria === filtroCat;
    const matchAlert =
      filtroAlert === 'Todos'       ? true :
      filtroAlert === 'StockBajo'   ? p.stock <= p.stock_minimo :
      filtroAlert === 'PorVencer'   ? (diasHasta(p.proximo_vencimiento) !== null && diasHasta(p.proximo_vencimiento) <= 30) :
      filtroAlert === 'Controlado'  ? p.es_controlado === 1 : true;
    return matchBus && matchCat && matchAlert;
  });

  // ── Contadores para badges ──────────────────────────────────
  const ctStockBajo  = productos.filter(p => p.stock <= p.stock_minimo).length;
  const ctPorVencer  = productos.filter(p => {
    const d = diasHasta(p.proximo_vencimiento);
    return d !== null && d <= 30;
  }).length;
  const ctControlado = productos.filter(p => p.es_controlado === 1).length;

  // ════════════════════════════════════════════════════════════
  //  ACCIONES
  // ════════════════════════════════════════════════════════════

  // ── Crear / editar producto ─────────────────────────────────
  const abrirNuevoProducto = () => {
    setFormProd(PROD_VACIO); setModalProducto('nuevo'); setMensaje(null);
  };
  const abrirEditarProducto = (p) => {
    setFormProd({
      nombre: p.nombre, codigo: p.codigo||'', unidad: p.unidad,
      unidad_despacho: p.unidad_despacho||'', factor_conversion: p.factor_conversion||1,
      stock_minimo: p.stock_minimo, precio_unitario: p.precio_unitario,
      es_controlado: p.es_controlado===1, categoria: p.categoria||'',
    });
    setModalProducto(p); setMensaje(null);
  };

  const guardarProducto = async () => {
    if (!formProd.nombre || !formProd.unidad) {
      aviso('err','Nombre y unidad son obligatorios'); return;
    }
    setGuardando(true);
    const payload = {
      ...formProd,
      factor_conversion: Number(formProd.factor_conversion)||1,
      stock_minimo:      Number(formProd.stock_minimo)||5,
      precio_unitario:   Number(formProd.precio_unitario)||0,
      es_controlado:     formProd.es_controlado ? 1 : 0,
    };
    const r = modalProducto === 'nuevo'
      ? await api.inventario.crear(payload)
      : await api.inventario.editar({ ...payload, id: modalProducto.id });
    setGuardando(false);
    if (r.ok) { setModalProducto(null); cargar(); aviso('ok', modalProducto==='nuevo'?'Producto creado':'Producto actualizado'); }
    else        aviso('err', r.error);
  };

  // ── Entrada de stock con lote ───────────────────────────────
  const abrirEntrada = (p) => { setFormEnt(ENT_VACIO); setModalEntrada(p); setMensaje(null); };

  const guardarEntrada = async () => {
    if (!formEnt.cantidad || Number(formEnt.cantidad) <= 0) {
      aviso('err','La cantidad debe ser mayor a cero'); return;
    }
    setGuardando(true);
    const r = await api.inventario.entrada({
      id:                modalEntrada.id,
      cantidad:          Number(formEnt.cantidad),
      costo_unitario:    Number(formEnt.costo_unitario)||0,
      numero_lote:       formEnt.numero_lote.trim() || null,
      fecha_vencimiento: formEnt.fecha_vencimiento || null,
      proveedor:         formEnt.proveedor.trim()   || null,
      factura_proveedor: formEnt.factura_proveedor.trim() || null,
      usuario_id:        usuario.id,
      observacion:       formEnt.observacion.trim() || null,
    });
    setGuardando(false);
    if (r.ok) {
      setModalEntrada(null);
      cargar();
      aviso('ok',
        `Stock actualizado: ${r.data.nuevoStock} unidades · ` +
        `Costo promedio: $${Number(r.data.costoProm).toFixed(4)}`
      );
    } else {
      aviso('err', r.error);
    }
  };

  // ── Ver lotes ───────────────────────────────────────────────
  const abrirLotes = async (p) => {
    const r = await api.inventario.lotesProducto(p.id);
    setModalLotes({ producto: p, lotes: r.ok ? r.data : [] });
  };

  // ── Kardex ──────────────────────────────────────────────────
  const abrirKardex = async (p) => {
    setModalKardex({ producto: p, movimientos: [] });
    const r = await api.inventario.kardex({
      inventario_id: p.id,
      desde: kardexDesde || null,
      hasta: kardexHasta || null,
      limite: 100,
    });
    setModalKardex({ producto: p, movimientos: r.ok ? r.data : [] });
  };

  const recargarKardex = async () => {
    if (!modalKardex) return;
    const r = await api.inventario.kardex({
      inventario_id: modalKardex.producto.id,
      desde: kardexDesde || null,
      hasta: kardexHasta || null,
      limite: 100,
    });
    setModalKardex(prev => ({ ...prev, movimientos: r.ok ? r.data : [] }));
  };

  // ── Ajuste ──────────────────────────────────────────────────
  const abrirAjuste = async (p) => {
    setFormAjuste(AJU_VACIO);
    setModalAjuste(p);
    // cargar lotes del producto para el selector
    const r = await api.inventario.lotesProducto(p.id);
    setModalAjuste({ ...p, _lotes: r.ok ? r.data.filter(l => l.activo===1) : [] });
    setMensaje(null);
  };

  const guardarAjuste = async () => {
    if (!formAjuste.cantidad || Number(formAjuste.cantidad) <= 0) {
      aviso('err','Cantidad debe ser mayor a cero'); return;
    }
    if (!formAjuste.motivo_ajuste.trim()) {
      aviso('err','Ingresa el motivo del ajuste'); return;
    }
    setGuardando(true);
    const r = await api.inventario.ajuste({
      inventario_id: modalAjuste.id,
      lote_id:       formAjuste.lote_id ? Number(formAjuste.lote_id) : null,
      tipo_ajuste:   formAjuste.tipo_ajuste,
      cantidad:      Number(formAjuste.cantidad),
      motivo_ajuste: formAjuste.motivo_ajuste.trim(),
      usuario_id:    usuario.id,
    });
    setGuardando(false);
    if (r.ok) {
      setModalAjuste(null);
      cargar();
      aviso('ok', `Ajuste aplicado. Stock nuevo: ${r.data.nuevoStock} unidades`);
    } else {
      aviso('err', r.error);
    }
  };

  // ════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          📦 Módulo de Inventario
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Productos · Stock · Lotes · Movimientos (Kardex)
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          {[
            { id:'productos', label:'📦 Productos' },
            { id:'kardex',    label:'📊 Kardex global' },
            { id:'reportes',  label:'📑 Reportes' },
          ].map(t => (
            <button key={t.id} onClick={() => setVista(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: vista === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      vista === t.id ? '#1B2A5E' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={S.pagina}>

      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      {/* ══════════════════════════════════════════════════════
          VISTA: PRODUCTOS
      ══════════════════════════════════════════════════════ */}
      {vista === 'productos' && (
        <div style={S.card}>
          <div style={S.cardTit}>
            <span>Inventario ({filtrados.length} productos)</span>
            {(usuario.rol === 'admin' || usuario.rol === 'farmacia') && (
              <button style={S.btnVerde} onClick={abrirNuevoProducto}>+ Nuevo producto</button>
            )}
          </div>

          {/* Filtros */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
            <input
              style={{ ...S.input, maxWidth:'240px' }}
              placeholder="Buscar nombre o código..."
              value={busqueda} onChange={e => setBusqueda(e.target.value)}
            />

            {/* Filtro por categoría */}
            {['Todos', ...CATEGORIAS].map(c => (
              <button key={c}
                style={{ ...S.btnSm(filtroCat===c ? C.azul : null), padding:'5px 12px', fontSize:'11px' }}
                onClick={() => setFiltroCat(c)}>{c}
              </button>
            ))}
          </div>

          {/* Filtros de alerta */}
          <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
            {[
              { id:'Todos',      label:'Todos' },
              { id:'StockBajo',  label:`Stock bajo (${ctStockBajo})`,   color:C.rojo },
              { id:'PorVencer',  label:`Por vencer (${ctPorVencer})`,   color:C.naranja },
              { id:'Controlado', label:`Controlados (${ctControlado})`, color:C.morado },
            ].map(f => (
              <button key={f.id}
                style={{
                  ...S.btnSm(filtroAlert===f.id ? (f.color||C.azul) : null),
                  padding:'5px 12px', fontSize:'11px',
                }}
                onClick={() => setFiltroAlert(f.id)}>{f.label}
              </button>
            ))}
          </div>

          {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'30px' }}>Cargando...</div>}

          {!cargando && (
            <div style={{ overflowX:'auto' }}>
              <table style={S.tabla}>
                <thead>
                  <tr>
                    {['Producto','Código','Cat.','Stock','Costo prom.','Px venta','Próx. vence','Lotes','Acciones'].map(h => (
                      <th key={h} style={S.th}>
                        {h}
                        {h === 'Costo prom.' && (
                          <TooltipAyuda texto="Se recalcula automáticamente cada vez que entra stock nuevo a un precio distinto. Es el promedio ponderado entre el costo anterior y el nuevo, usado para valorar el inventario." />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map(p => {
                    const colorS    = colorStock(p.stock, p.stock_minimo);
                    const pctStock  = p.stock_minimo > 0 ? (p.stock / (p.stock_minimo * 3)) * 100 : 50;
                    const colorV    = colorVencimiento(p.proximo_vencimiento);
                    const diasV     = diasHasta(p.proximo_vencimiento);

                    return (
                      <tr key={p.id}
                        onMouseEnter={e => e.currentTarget.style.background='#f8fafd'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>

                        {/* Producto */}
                        <td style={S.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                            <strong style={{ color:C.azul }}>{p.nombre}</strong>
                            {p.es_controlado===1 && (
                              <span style={{ ...S.badge(C.morado), fontSize:'9px' }}>CONTROLADO</span>
                            )}
                          </div>
                          <div style={{ fontSize:'11px', color:C.gris, marginTop:'2px' }}>
                            {p.unidad}
                            {p.factor_conversion > 1 && ` · 1 ${p.unidad_despacho||'disp.'} = ${p.factor_conversion} ${p.unidad}`}
                          </div>
                        </td>

                        {/* Código */}
                        <td style={{ ...S.td, color:C.gris, fontSize:'11px' }}>{p.codigo||'—'}</td>

                        {/* Categoría */}
                        <td style={S.td}>
                          {p.categoria
                            ? <span style={S.pill(p.categoria)}>{p.categoria}</span>
                            : <span style={{ color:'#ccc' }}>—</span>
                          }
                        </td>

                        {/* Stock */}
                        <td style={{ ...S.td, minWidth:'120px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
                            <span style={{ fontWeight:'700', color: colorS, fontSize:'13px' }}>{p.stock}</span>
                            <span style={{ fontSize:'11px', color:C.gris }}>mín: {p.stock_minimo}</span>
                          </div>
                          <div style={{ background:'#eef1f6', borderRadius:'99px', height:'5px' }}>
                            <div style={S.stockBar(pctStock, colorS)} />
                          </div>
                          {p.stock <= 0 && (
                            <div style={{ fontSize:'10px', color:C.rojo, fontWeight:'700', marginTop:'2px' }}>SIN STOCK</div>
                          )}
                        </td>

                        {/* Costo promedio */}
                        <td style={{ ...S.td, fontWeight:'600', color:'#495057' }}>
                          {fmtMonto(p.costo_promedio)}
                        </td>

                        {/* Precio venta */}
                        <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>
                          {fmtMonto(p.precio_unitario)}
                        </td>

                        {/* Próximo vencimiento */}
                        <td style={S.td}>
                          {p.proximo_vencimiento ? (
                            <div>
                              <span style={{ color: colorV||C.gris, fontWeight:'600', fontSize:'12px' }}>
                                {fmtFecha(p.proximo_vencimiento)}
                              </span>
                              {diasV !== null && (
                                <div style={{ fontSize:'10px', color: colorV||C.gris }}>
                                  {diasV < 0 ? `Venció hace ${-diasV}d` : `${diasV}d restantes`}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color:'#ccc' }}>—</span>
                          )}
                        </td>

                        {/* Lotes */}
                        <td style={{ ...S.td, textAlign:'center' }}>
                          <span style={{ ...S.badge(C.azul), fontSize:'11px' }}>
                            {p.total_lotes||0}
                          </span>
                        </td>

                        {/* Acciones */}
                        <td style={{ ...S.td, minWidth:'220px' }}>
                          <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                            {(usuario.rol==='admin' || usuario.rol==='farmacia' || usuario.rol==='enfermeria') && (
                              <button style={S.btnSm(C.verde)} onClick={() => abrirEntrada(p)}>
                                + Entrada
                              </button>
                            )}
                            <button style={S.btnSm(C.azul)} onClick={() => abrirLotes(p)}>
                              Lotes
                            </button>
                            <button style={S.btnSm('#888')} onClick={() => abrirKardex(p)}>
                              Kardex
                            </button>
                            {(usuario.rol==='admin' || usuario.rol==='farmacia') && (
                              <>
                                <button style={S.btnSm(C.naranja)} onClick={() => abrirAjuste(p)}>
                                  Ajuste
                                </button>
                                <button style={S.btnSm()} onClick={() => abrirEditarProducto(p)}>
                                  Editar
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtrados.length === 0 && (
                    <tr>
                      <td colSpan="9" style={{ ...S.td, textAlign:'center', color:C.gris, padding:'40px' }}>
                        No se encontraron productos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VISTA: KARDEX GLOBAL (buscar y ver movimientos)
      ══════════════════════════════════════════════════════ */}
      {vista === 'kardex' && (
        <KardexGlobal usuario={usuario} productos={productos} />
      )}

      {/* ══════════════════════════════════════════════════════
          VISTA: REPORTES (Inventario valor · Kardex por producto · Controlados)
          Trasladado desde el módulo de Reportes general — pertenece
          al dominio de bodega/farmacia, no al financiero-contable.
      ══════════════════════════════════════════════════════ */}
      {vista === 'reportes' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
          <SeccionReporteInventario />
          <SeccionReporteKardex />
          <SeccionReporteControlados />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MODAL: NUEVO / EDITAR PRODUCTO
      ══════════════════════════════════════════════════════ */}
      {modalProducto !== null && (
        <ModalProducto
          modal={modalProducto}
          form={formProd}
          setForm={setFormProd}
          guardando={guardando}
          onGuardar={guardarProducto}
          onCerrar={() => setModalProducto(null)}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          MODAL: ENTRADA DE STOCK
      ══════════════════════════════════════════════════════ */}
      {modalEntrada && (
        <ModalEntrada
          producto={modalEntrada}
          form={formEnt}
          setForm={setFormEnt}
          guardando={guardando}
          onGuardar={guardarEntrada}
          onCerrar={() => setModalEntrada(null)}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          MODAL: LOTES
      ══════════════════════════════════════════════════════ */}
      {modalLotes && (
        <ModalLotes
          data={modalLotes}
          onCerrar={() => setModalLotes(null)}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          MODAL: KARDEX DEL PRODUCTO
      ══════════════════════════════════════════════════════ */}
      {modalKardex && (
        <ModalKardex
          data={modalKardex}
          desde={kardexDesde} setDesde={setKardexDesde}
          hasta={kardexHasta} setHasta={setKardexHasta}
          onRecargar={recargarKardex}
          onCerrar={() => setModalKardex(null)}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          MODAL: AJUSTE MANUAL
      ══════════════════════════════════════════════════════ */}
      {modalAjuste && (
        <ModalAjuste
          producto={modalAjuste}
          form={formAjuste}
          setForm={setFormAjuste}
          guardando={guardando}
          onGuardar={guardarAjuste}
          onCerrar={() => setModalAjuste(null)}
        />
      )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  SUB-COMPONENTE: KARDEX GLOBAL
// ════════════════════════════════════════════════════════════
function KardexGlobal({ usuario, productos }) {
  const [selProd,  setSelProd]  = useState('');
  const [desde,    setDesde]    = useState('');
  const [hasta,    setHasta]    = useState('');
  const [movs,     setMovs]     = useState([]);
  const [cargando, setCargando] = useState(false);

  const cargar = async () => {
    if (!selProd) return;
    setCargando(true);
    const r = await api.inventario.kardex({
      inventario_id: Number(selProd),
      desde: desde||null, hasta: hasta||null, limite: 200,
    });
    if (r.ok) setMovs(r.data);
    setCargando(false);
  };

  const COLOR_MOV = {
    Entrada:'#1D9E75', Salida:'#dc3545', Ajuste:'#e67e22',
    Devolucion:'#6f42c1', Vencimiento:'#6c757d',
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}><span>Kardex global de movimientos</span></div>
      <div style={{ display:'flex', gap:'10px', marginBottom:'16px', flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ flex:'2', minWidth:'200px' }}>
          <label style={S.label}>Producto</label>
          <select style={S.input} value={selProd} onChange={e => setSelProd(e.target.value)}>
            <option value="">— Selecciona un producto —</option>
            {productos.map(p => (
              <option key={p.id} value={p.id}>{p.nombre} ({p.codigo||p.id})</option>
            ))}
          </select>
        </div>
        <div style={{ minWidth:'140px' }}>
          <label style={S.label}>Desde</label>
          <input type="date" style={S.input} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={{ minWidth:'140px' }}>
          <label style={S.label}>Hasta</label>
          <input type="date" style={S.input} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <button style={{ ...S.btnAzul, alignSelf:'flex-end' }} onClick={cargar} disabled={!selProd}>
          Cargar
        </button>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Cargando...</div>}

      {!cargando && movs.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={S.tabla}>
            <thead>
              <tr>
                {['Fecha','Tipo','Referencia','Lote','Cantidad','Costo unit.','Stock ant.','Stock nuevo','Paciente','Usuario'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movs.map(m => (
                <tr key={m.id}
                  onMouseEnter={e => e.currentTarget.style.background='#f8fafd'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ ...S.td, fontSize:'11px', whiteSpace:'nowrap' }}>
                    {m.fecha ? new Date(m.fecha).toLocaleString('es-SV') : '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge(COLOR_MOV[m.tipo_mov]||C.gris), fontSize:'10px' }}>
                      {m.tipo_mov}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>
                    {m.tipo_referencia||'—'}
                  </td>
                  <td style={{ ...S.td, fontSize:'11px' }}>
                    {m.numero_lote ? (
                      <div>
                        <span style={{ fontWeight:'600' }}>{m.numero_lote}</span>
                        {m.lote_vencimiento && (
                          <div style={{ fontSize:'10px', color: colorVencimiento(m.lote_vencimiento)||C.gris }}>
                            Vence: {fmtFecha(m.lote_vencimiento)}
                          </div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ ...S.td, fontWeight:'700',
                    color: m.tipo_mov==='Entrada'||m.tipo_mov==='Devolucion' ? C.verde : C.rojo }}>
                    {m.tipo_mov==='Entrada'||m.tipo_mov==='Devolucion'||
                     m.tipo_referencia==='Ajuste_Alta'  ? '+' : '−'}
                    {m.cantidad}
                  </td>
                  <td style={{ ...S.td, fontSize:'12px' }}>
                    {m.costo_unitario != null ? fmtMonto(m.costo_unitario) : '—'}
                  </td>
                  <td style={{ ...S.td, color:C.gris, fontSize:'12px' }}>{m.stock_anterior}</td>
                  <td style={{ ...S.td, fontWeight:'600', color:C.azul, fontSize:'12px' }}>{m.stock_nuevo}</td>
                  <td style={{ ...S.td, fontSize:'11px' }}>{m.paciente_nombre||'—'}</td>
                  <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{m.usuario_nombre||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize:'11px', color:C.gris, marginTop:'8px', textAlign:'right' }}>
            {movs.length} movimientos
          </div>
        </div>
      )}
      {!cargando && selProd && movs.length === 0 && (
        <p style={{ color:C.gris, fontSize:'13px' }}>Sin movimientos para este producto en el rango seleccionado.</p>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: NUEVO / EDITAR PRODUCTO
// ════════════════════════════════════════════════════════════
function ModalProducto({ modal, form, setForm, guardando, onGuardar, onCerrar }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const esNuevo = modal === 'nuevo';
  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={S.modalCard('520px')} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:'15px', fontWeight:'800', color:C.azul, marginBottom:'20px' }}>
          {esNuevo ? 'Nuevo producto / medicamento' : `Editar: ${modal.nombre}`}
        </div>

        <div style={S.fila2}>
          <div style={{ ...S.campo, gridColumn:'span 2' }}>
            <label style={S.label}>Nombre *</label>
            <input style={S.input} value={form.nombre}
              onChange={e => set('nombre', e.target.value)} placeholder="Nombre del producto" />
          </div>
        </div>

        <div style={S.fila3}>
          {esNuevo && (
            <div style={S.campo}>
              <label style={S.label}>Código</label>
              <input style={S.input} value={form.codigo}
                onChange={e => set('codigo', e.target.value)} placeholder="MED-001" />
            </div>
          )}
          <div style={S.campo}>
            <label style={S.label}>Unidad base *</label>
            <input style={S.input} value={form.unidad}
              onChange={e => set('unidad', e.target.value)} placeholder="tableta / ml / ampolla" />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Unidad despacho</label>
            <input style={S.input} value={form.unidad_despacho}
              onChange={e => set('unidad_despacho', e.target.value)} placeholder="caja / frasco" />
          </div>
          {!esNuevo && <div style={S.campo} />}
        </div>

        <div style={S.fila3}>
          <div style={S.campo}>
            <label style={S.label}>Factor conversión</label>
            <input type="number" min="1" style={S.input} value={form.factor_conversion}
              onChange={e => set('factor_conversion', e.target.value)}
              title="1 unidad despacho = ? unidades base" />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Stock mínimo</label>
            <input type="number" min="0" step="1" style={S.input} value={form.stock_minimo}
              onChange={e => set('stock_minimo', e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Precio venta ($)</label>
            <input type="number" min="0" step="0.01" style={S.input} value={form.precio_unitario}
              onChange={e => set('precio_unitario', e.target.value)} />
          </div>
        </div>

        <div style={S.fila2}>
          <div style={S.campo}>
            <label style={S.label}>Categoría</label>
            <select style={S.input} value={form.categoria}
              onChange={e => set('categoria', e.target.value)}>
              <option value="">— Sin categoría —</option>
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ ...S.campo, display:'flex', alignItems:'center', gap:'8px', paddingTop:'20px' }}>
            <input type="checkbox" id="ctrlchk" style={{ width:'16px', height:'16px', cursor:'pointer' }}
              checked={form.es_controlado}
              onChange={e => set('es_controlado', e.target.checked)} />
            <label htmlFor="ctrlchk" style={{ fontSize:'13px', fontWeight:'600', color:C.morado, cursor:'pointer' }}>
              Medicamento controlado
            </label>
          </div>
        </div>

        <div style={{ display:'flex', gap:'10px', marginTop:'6px' }}>
          <button style={S.btnVerde} onClick={onGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
          <button style={S.btnGris} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: ENTRADA DE STOCK (con lote)
// ════════════════════════════════════════════════════════════
function ModalEntrada({ producto, form, setForm, guardando, onGuardar, onCerrar }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const costoTotal = form.cantidad && form.costo_unitario
    ? (Number(form.cantidad) * Number(form.costo_unitario)).toFixed(2)
    : null;

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={S.modalCard('500px')} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:'15px', fontWeight:'800', color:C.azul, marginBottom:'4px' }}>
          Entrada de stock
        </div>
        <div style={{ fontSize:'12px', color:C.gris, marginBottom:'20px' }}>
          {producto.nombre} · Stock actual: <strong>{producto.stock} {producto.unidad}</strong>
        </div>

        {producto.es_controlado === 1 && (
          <div style={{ ...S.alerta('warn'), fontSize:'12px' }}>
            ⚠️ Medicamento controlado — verifica documentación de compra antes de ingresar.
          </div>
        )}

        <div style={S.fila2}>
          <div style={S.campo}>
            <label style={S.label}>Cantidad * ({producto.unidad})</label>
            <input type="number" min="0.001" step="any" style={S.input}
              value={form.cantidad} onChange={e => set('cantidad', e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Costo unitario ($)</label>
            <input type="number" min="0" step="0.0001" style={S.input}
              value={form.costo_unitario} onChange={e => set('costo_unitario', e.target.value)} />
          </div>
        </div>

        {costoTotal && (
          <div style={{ ...S.alerta('ok'), padding:'8px 14px', fontSize:'12px', marginBottom:'12px' }}>
            Costo total de esta entrada: <strong>${costoTotal}</strong>
            {producto.costo_promedio > 0 && (
              <span style={{ marginLeft:'8px', color:'#155724', opacity:.8 }}>
                · Costo promedio actual: ${Number(producto.costo_promedio).toFixed(4)}
              </span>
            )}
          </div>
        )}

        <div style={S.fila2}>
          <div style={S.campo}>
            <label style={S.label}>Número de lote</label>
            <input style={S.input} value={form.numero_lote}
              onChange={e => set('numero_lote', e.target.value)}
              placeholder="Ej: L-2025-001 (opcional)" />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Fecha de vencimiento</label>
            <input type="date" style={S.input} value={form.fecha_vencimiento}
              onChange={e => set('fecha_vencimiento', e.target.value)} />
          </div>
        </div>

        <div style={S.fila2}>
          <div style={S.campo}>
            <label style={S.label}>Proveedor</label>
            <input style={S.input} value={form.proveedor}
              onChange={e => set('proveedor', e.target.value)} placeholder="Nombre del proveedor" />
          </div>
          <div style={S.campo}>
            <label style={S.label}>N° factura proveedor</label>
            <input style={S.input} value={form.factura_proveedor}
              onChange={e => set('factura_proveedor', e.target.value)} placeholder="Ej: FAC-00123" />
          </div>
        </div>

        <div style={S.campo}>
          <label style={S.label}>Observación</label>
          <input style={S.input} value={form.observacion}
            onChange={e => set('observacion', e.target.value)}
            placeholder="Notas adicionales (opcional)" />
        </div>

        <div style={{ display:'flex', gap:'10px', marginTop:'6px' }}>
          <button style={S.btnVerde} onClick={onGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : '✓ Registrar entrada'}
          </button>
          <button style={S.btnGris} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: LOTES DEL PRODUCTO
// ════════════════════════════════════════════════════════════
function ModalLotes({ data, onCerrar }) {
  const { producto, lotes } = data;
  const activosTotal = lotes.filter(l => l.activo===1).reduce((s,l) => s+l.cantidad_actual, 0);

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={S.modalCard('700px')} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
          <div>
            <div style={{ fontSize:'15px', fontWeight:'800', color:C.azul }}>
              Lotes — {producto.nombre}
            </div>
            <div style={{ fontSize:'12px', color:C.gris, marginTop:'2px' }}>
              {lotes.length} lotes · Stock total en lotes activos: <strong>{activosTotal} {producto.unidad}</strong>
            </div>
          </div>
          <button style={S.btnGris} onClick={onCerrar}>Cerrar</button>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={S.tabla}>
            <thead>
              <tr>
                {['Número de lote','Ingresado','Inicial','Disponible','Costo unit.','Vencimiento','Días','Proveedor','Factura','Estado'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lotes.map(l => {
                const dias = diasHasta(l.fecha_vencimiento);
                const colorV = colorVencimiento(l.fecha_vencimiento);
                return (
                  <tr key={l.id}
                    style={{ opacity: l.activo===0 ? .5 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>{l.numero_lote}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris, whiteSpace:'nowrap' }}>
                      {l.fecha_entrada ? new Date(l.fecha_entrada).toLocaleDateString('es-SV') : '—'}
                    </td>
                    <td style={S.td}>{l.cantidad_inicial}</td>
                    <td style={{ ...S.td, fontWeight:'700', color: l.cantidad_actual<=0?C.rojo:C.verde }}>
                      {l.cantidad_actual}
                    </td>
                    <td style={S.td}>{fmtMonto(l.costo_unitario)}</td>
                    <td style={{ ...S.td, color: colorV||C.gris, fontWeight:'600', fontSize:'12px', whiteSpace:'nowrap' }}>
                      {fmtFecha(l.fecha_vencimiento)}
                    </td>
                    <td style={{ ...S.td, fontSize:'11px', color: colorV||C.gris }}>
                      {dias === null ? '—' :
                       dias < 0 ? `Venció` :
                       `${dias}d`}
                    </td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{l.proveedor||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{l.factura_proveedor||'—'}</td>
                    <td style={S.td}>
                      <span style={S.badge(l.activo===1 ? C.verde : C.gris)}>
                        {l.activo===1 ? 'Activo' : 'Agotado'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {lotes.length === 0 && (
                <tr><td colSpan="10" style={{ ...S.td, textAlign:'center', color:C.gris, padding:'30px' }}>
                  Sin lotes registrados.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: KARDEX DEL PRODUCTO
// ════════════════════════════════════════════════════════════
function ModalKardex({ data, desde, setDesde, hasta, setHasta, onRecargar, onCerrar }) {
  const { producto, movimientos } = data;
  const COLOR_MOV = {
    Entrada:'#1D9E75', Salida:'#dc3545', Ajuste:'#e67e22',
    Devolucion:'#6f42c1', Vencimiento:'#6c757d',
  };
  const esPositivo = (m) =>
    m.tipo_mov === 'Entrada' || m.tipo_mov === 'Devolucion' ||
    m.tipo_referencia === 'Ajuste_Alta';

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={S.modalCard('860px')} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'15px', fontWeight:'800', color:C.azul }}>
              Kardex — {producto.nombre}
            </div>
            <div style={{ fontSize:'12px', color:C.gris, marginTop:'2px' }}>
              {producto.codigo || producto.id} · {producto.unidad}
            </div>
          </div>
          <button style={S.btnGris} onClick={onCerrar}>Cerrar</button>
        </div>

        {/* Filtros de fecha */}
        <div style={{ display:'flex', gap:'10px', marginBottom:'14px', alignItems:'flex-end' }}>
          <div>
            <label style={S.label}>Desde</label>
            <input type="date" style={{ ...S.input, width:'150px' }}
              value={desde} onChange={e => setDesde(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Hasta</label>
            <input type="date" style={{ ...S.input, width:'150px' }}
              value={hasta} onChange={e => setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={onRecargar}>Filtrar</button>
          <span style={{ fontSize:'11px', color:C.gris, alignSelf:'center' }}>
            {movimientos.length} movimientos
          </span>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={S.tabla}>
            <thead>
              <tr>
                {['Fecha','Tipo','Detalle','Lote','Cantidad','Costo','Stock ant.','Stock nuevo','Motivo / Paciente','Usuario'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movimientos.map(m => (
                <tr key={m.id}
                  onMouseEnter={e => e.currentTarget.style.background='#f8fafd'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ ...S.td, fontSize:'11px', whiteSpace:'nowrap', color:C.gris }}>
                    {m.fecha ? new Date(m.fecha).toLocaleString('es-SV') : '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge(COLOR_MOV[m.tipo_mov]||C.gris), fontSize:'10px' }}>
                      {m.tipo_mov}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>
                    {m.tipo_referencia||'—'}
                  </td>
                  <td style={{ ...S.td, fontSize:'11px' }}>
                    {m.numero_lote ? (
                      <div>
                        <span style={{ fontWeight:'600', color:C.azul }}>{m.numero_lote}</span>
                        {m.lote_vencimiento && (
                          <div style={{ fontSize:'10px', color: colorVencimiento(m.lote_vencimiento)||C.gris }}>
                            {fmtFecha(m.lote_vencimiento)}
                          </div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ ...S.td, fontWeight:'700', textAlign:'right',
                    color: esPositivo(m) ? C.verde : C.rojo }}>
                    {esPositivo(m) ? '+' : '−'}{m.cantidad}
                  </td>
                  <td style={{ ...S.td, fontSize:'12px', textAlign:'right' }}>
                    {m.costo_unitario != null ? fmtMonto(m.costo_unitario) : '—'}
                  </td>
                  <td style={{ ...S.td, color:C.gris, textAlign:'right' }}>{m.stock_anterior}</td>
                  <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{m.stock_nuevo}</td>
                  <td style={{ ...S.td, fontSize:'11px' }}>
                    {m.motivo_ajuste || m.paciente_nombre || '—'}
                  </td>
                  <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{m.usuario_nombre||'—'}</td>
                </tr>
              ))}
              {movimientos.length === 0 && (
                <tr><td colSpan="10" style={{ ...S.td, textAlign:'center', color:C.gris, padding:'30px' }}>
                  Sin movimientos en el rango seleccionado.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: AJUSTE MANUAL
// ════════════════════════════════════════════════════════════
function ModalAjuste({ producto, form, setForm, guardando, onGuardar, onCerrar }) {
  const set   = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const lotes = producto._lotes || [];
  const esAlta = form.tipo_ajuste === 'Ajuste_Alta';

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={S.modalCard('460px')} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:'15px', fontWeight:'800', color:C.azul, marginBottom:'4px', display:'flex', alignItems:'center' }}>
          Ajuste de stock
          <TooltipAyuda texto="Usa esto solo para corregir el stock sin un movimiento normal (ej. conteo físico distinto, producto dañado o vencido). Para compras con factura, usa 'Entrada de stock'." />
        </div>
        <div style={{ fontSize:'12px', color:C.gris, marginBottom:'20px' }}>
          {producto.nombre} · Stock actual: <strong>{producto.stock} {producto.unidad}</strong>
        </div>

        {/* Tipo de ajuste */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'14px' }}>
          {[
            { v:'Ajuste_Alta', label:'+ Alta de stock', color:C.verde },
            { v:'Ajuste_Baja', label:'− Baja de stock', color:C.rojo  },
          ].map(t => (
            <button key={t.v}
              style={{
                flex:1, padding:'10px', borderRadius:'8px', fontSize:'13px',
                fontWeight:'700', cursor:'pointer',
                border: `2px solid ${form.tipo_ajuste===t.v ? t.color : '#dde3ea'}`,
                background: form.tipo_ajuste===t.v ? t.color+'11' : '#fff',
                color: form.tipo_ajuste===t.v ? t.color : C.gris,
              }}
              onClick={() => set('tipo_ajuste', t.v)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Lote específico (opcional) */}
        {lotes.length > 0 && (
          <div style={S.campo}>
            <label style={S.label}>Aplicar a lote específico (opcional)</label>
            <select style={S.input} value={form.lote_id}
              onChange={e => set('lote_id', e.target.value)}>
              <option value="">— Stock general (sin lote) —</option>
              {lotes.map(l => (
                <option key={l.id} value={l.id}>
                  {l.numero_lote} · Disponible: {l.cantidad_actual}
                  {l.fecha_vencimiento ? ` · Vence: ${fmtFecha(l.fecha_vencimiento)}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={S.campo}>
          <label style={S.label}>Cantidad a ajustar *</label>
          <input type="number" min="0.001" step="any" style={S.input}
            value={form.cantidad} onChange={e => set('cantidad', e.target.value)} />
          {form.cantidad && producto.stock && (
            <div style={{ fontSize:'11px', marginTop:'4px',
              color: esAlta ? C.verde : (producto.stock - Number(form.cantidad) < 0 ? C.rojo : C.gris) }}>
              Stock resultante: {esAlta
                ? Number(producto.stock) + Number(form.cantidad)
                : Number(producto.stock) - Number(form.cantidad)
              } {producto.unidad}
            </div>
          )}
        </div>

        <div style={S.campo}>
          <label style={S.label}>Motivo del ajuste *</label>
          <select style={{ ...S.input, marginBottom:'6px' }}
            value={form.motivo_ajuste}
            onChange={e => set('motivo_ajuste', e.target.value)}>
            <option value="">— Seleccionar motivo —</option>
            {esAlta ? [
              'Corrección de conteo', 'Ingreso no registrado', 'Devolución de servicio',
              'Inventario físico', 'Otro',
            ] : [
              'Medicamento vencido', 'Medicamento dañado', 'Pérdida / robo',
              'Muestra médica', 'Corrección de conteo', 'Inventario físico', 'Otro',
            ].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {form.motivo_ajuste === 'Otro' && (
            <input style={S.input} placeholder="Describe el motivo..."
              onChange={e => set('motivo_ajuste', e.target.value)} />
          )}
        </div>

        <div style={{ display:'flex', gap:'10px', marginTop:'6px' }}>
          <button
            style={{ ...(esAlta ? S.btnVerde : S.btnRojo) }}
            onClick={onGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : `✓ Confirmar ${esAlta?'alta':'baja'}`}
          </button>
          <button style={S.btnGris} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
// ════════════════════════════════════════════════════════════
//  5. INVENTARIO — VALOR EN BODEGA
// ════════════════════════════════════════════════════════════
function SeccionReporteInventario() {
  const [datos,    setDatos]    = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtro,   setFiltro]   = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await api.reportes.inventarioValor();
    if (r.ok) setDatos(r.data);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = datos.filter(d =>
    !filtro || d.nombre.toLowerCase().includes(filtro.toLowerCase())
             || (d.codigo||'').toLowerCase().includes(filtro.toLowerCase())
             || (d.categoria||'').toLowerCase().includes(filtro.toLowerCase())
  );

  const totalCosto  = filtrados.reduce((s,d) => s+Number(d.valor_costo||0),0);
  const totalVenta  = filtrados.reduce((s,d) => s+Number(d.valor_venta||0),0);
  const sinStock    = filtrados.filter(d => d.stock <= 0).length;
  const stockBajo   = filtrados.filter(d => d.stock > 0 && d.stock <= d.stock_minimo).length;

  const COLS_CSV = [
    { key:'codigo',            label:'Código'           },
    { key:'nombre',            label:'Producto'         },
    { key:'categoria',         label:'Categoría'        },
    { key:'unidad',            label:'Unidad'           },
    { key:'stock',             label:'Stock'            },
    { key:'stock_minimo',      label:'Stock mínimo'     },
    { key:'costo_promedio',    label:'Costo prom. ($)'  },
    { key:'valor_costo',       label:'Valor costo ($)'  },
    { key:'precio_unitario',   label:'Precio venta ($)' },
    { key:'valor_venta',       label:'Valor venta ($)'  },
    { key:'lotes_activos',     label:'Lotes activos'    },
    { key:'proximo_vencimiento', label:'Próx. vencimiento'},
  ];

  const imprimirPDF = () => {
    const filas = filtrados.map(d => {
      const colorStock = d.stock<=0?'#dc3545': d.stock<=d.stock_minimo?'#e67e22':'#155724';
      return `<tr>
        <td>${d.codigo||'—'}</td>
        <td>${d.nombre}</td>
        <td>${d.categoria||'—'}</td>
        <td style="text-align:right;color:${colorStock};font-weight:700">${d.stock} ${d.unidad}</td>
        <td style="text-align:right">${fmtMonto(d.costo_promedio)}</td>
        <td style="text-align:right">${fmtMonto(d.valor_costo)}</td>
        <td style="text-align:right">${fmtMonto(d.precio_unitario)}</td>
        <td style="text-align:right;font-weight:700">${fmtMonto(d.valor_venta)}</td>
        <td style="text-align:center">${d.lotes_activos}</td>
        <td>${fmtFecha(d.proximo_vencimiento)}</td>
      </tr>`;
    }).join('');
    imprimirHTML(`
      <h2>Inventario — Valor en bodega</h2>
      <p style="color:#6c757d;margin-bottom:12px">
        ${filtrados.length} productos · Generado: ${new Date().toLocaleDateString('es-SV')}
      </p>
      <table>
        <thead><tr>
          <th>Código</th><th>Producto</th><th>Cat.</th><th>Stock</th>
          <th>Costo prom.</th><th>Val. costo</th><th>Precio vta.</th><th>Val. venta</th>
          <th>Lotes</th><th>Próx. vence</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
      <div class="total-box">
        <span>Valor total en costo · Valor venta: ${fmtMonto(totalVenta)}</span>
        <span>${fmtMonto(totalCosto)}</span>
      </div>
    `, 'Inventario Valor Bodega');
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Valor en bodega ({filtrados.length} productos)</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <input style={{ ...S.input, width:'200px' }} placeholder="Filtrar..."
            value={filtro} onChange={e=>setFiltro(e.target.value)} />
          <button style={S.btnAzul} onClick={cargar}>↻</button>
          {datos.length>0 && <>
            <button style={S.btnVerde} onClick={() => exportarCSV(filtrados, COLS_CSV, 'Inventario_Valor')}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }}>
        {[
          { label:'Valor en costo',   valor:fmtMonto(totalCosto),  color:C.azul   },
          { label:'Valor en venta',   valor:fmtMonto(totalVenta),  color:C.verde  },
          { label:'Sin stock',        valor:sinStock,         color:C.rojo   },
          { label:'Stock bajo',       valor:stockBajo,        color:C.naranja},
        ].map(k => (
          <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
            <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
            <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
          </div>
        ))}
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Cargando...</div>}

      {!cargando && (
        <div style={{ overflowX:'auto' }}>
          <table style={S.tabla}>
            <thead>
              <tr>
                {['Código','Producto','Cat.','Stock','Stock mín.','Costo prom.','Val. costo','Px venta','Val. venta','Lotes','Próx. vence'].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.map((d,i) => {
                const colorS = d.stock<=0?C.rojo: d.stock<=d.stock_minimo?C.naranja:C.verde;
                const diasV  = d.proximo_vencimiento
                  ? Math.round((new Date(d.proximo_vencimiento+'T00:00:00')-new Date())/(1000*60*60*24))
                  : null;
                const colorV = diasV===null?null: diasV<0?C.rojo: diasV<=7?C.rojo: diasV<=30?C.naranja:C.verde;
                return (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{d.codigo||'—'}</td>
                    <td style={S.td}><strong>{d.nombre}</strong></td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{d.categoria||'—'}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:colorS }}>{d.stock} {d.unidad}</td>
                    <td style={{ ...S.td, color:C.gris }}>{d.stock_minimo}</td>
                    <td style={S.td}>{fmtMonto(d.costo_promedio)}</td>
                    <td style={{ ...S.td, fontWeight:'600', color:C.azul }}>{fmtMonto(d.valor_costo)}</td>
                    <td style={S.td}>{fmtMonto(d.precio_unitario)}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.verde }}>{fmtMonto(d.valor_venta)}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{d.lotes_activos}</td>
                    <td style={{ ...S.td, color:colorV||C.gris, fontWeight:colorV?'700':'400' }}>
                      {d.proximo_vencimiento ? (
                        <div>
                          <div>{fmtFecha(d.proximo_vencimiento)}</div>
                          <div style={{ fontSize:'10px' }}>
                            {diasV<0?`Venció hace ${-diasV}d`:`${diasV}d`}
                          </div>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
              {filtrados.length === 0 && (
                <tr><td colSpan="11" style={{ ...S.td, textAlign:'center', color:C.gris, padding:'30px' }}>
                  Sin productos.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!cargando && filtrados.length > 0 && (
        <div style={{ display:'flex', gap:'16px', marginTop:'10px' }}>
          <div style={{ ...S.totalBox, flex:1 }}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Valor total en costo</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{fmtMonto(totalCosto)}</span>
          </div>
          <div style={{ ...S.totalBox, flex:1, background:C.verde }}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Valor total en venta</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{fmtMonto(totalVenta)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  5. KARDEX POR PRODUCTO
// ════════════════════════════════════════════════════════════
function SeccionReporteKardex() {
  const [productos,   setProductos]   = useState([]);
  const [selProd,     setSelProd]     = useState('');
  const [desde,       setDesde]       = useState(primerDiaMes());
  const [hasta,       setHasta]       = useState(hoy());
  const [movs,        setMovs]        = useState([]);
  const [cargando,    setCargando]    = useState(false);
  const [buscado,     setBuscado]     = useState(false);

  useEffect(() => {
    api.inventario.listar().then(r => { if (r.ok) setProductos(r.data); });
  }, []);

  const cargar = async () => {
    if (!selProd) return;
    setCargando(true); setBuscado(true);
    const r = await api.reportes.kardexRango({
      inventario_id: Number(selProd), desde, hasta,
    });
    if (r.ok) setMovs(r.data);
    setCargando(false);
  };

  const prodInfo = productos.find(p => p.id === Number(selProd));

  const entradas   = movs.filter(m => m.tipo_mov==='Entrada').reduce((s,m)=>s+m.cantidad,0);
  const salidas    = movs.filter(m => m.tipo_mov==='Salida').reduce((s,m)=>s+m.cantidad,0);
  const ajustes    = movs.filter(m => m.tipo_mov==='Ajuste').reduce((s,m)=>s+m.cantidad,0);

  const COLOR_MOV = { Entrada:C.verde, Salida:C.rojo, Ajuste:C.naranja, Devolucion:C.morado, Vencimiento:C.gris };
  const esPos = m => m.tipo_mov==='Entrada'||m.tipo_mov==='Devolucion'||m.tipo_referencia==='Ajuste_Alta';

  const COLS_CSV = [
    { key:'fecha',          label:'Fecha/Hora'      },
    { key:'tipo_mov',       label:'Tipo'            },
    { key:'tipo_referencia',label:'Referencia'      },
    { key:'numero_lote',    label:'Lote'            },
    { key:'lote_vencimiento',label:'Vence lote'     },
    { key:'cantidad',       label:'Cantidad'        },
    { key:'costo_unitario', label:'Costo unit. ($)' },
    { key:'stock_anterior', label:'Stock anterior'  },
    { key:'stock_nuevo',    label:'Stock nuevo'     },
    { key:'motivo_ajuste',  label:'Motivo'          },
    { key:'paciente',       label:'Paciente'        },
    { key:'usuario_nombre', label:'Usuario'         },
  ];

  const imprimirPDF = () => {
    const signo = m => esPos(m)?'+':'−';
    const filas = movs.map(m=>`<tr>
      <td style="white-space:nowrap;font-size:10px">${fmtDT(m.fecha)}</td>
      <td><span style="background:${COLOR_MOV[m.tipo_mov]||C.gris}22;color:${COLOR_MOV[m.tipo_mov]||C.gris};padding:1px 5px;border-radius:4px;font-size:10px">${m.tipo_mov}</span></td>
      <td style="font-size:10px">${m.numero_lote||'—'}</td>
      <td style="color:${esPos(m)?'#155724':'#721c24'};font-weight:700;text-align:right">${signo(m)}${m.cantidad}</td>
      <td style="text-align:right">${m.costo_unitario!=null?fmtMonto(m.costo_unitario):'—'}</td>
      <td style="text-align:right">${m.stock_anterior}</td>
      <td style="text-align:right;font-weight:700">${m.stock_nuevo}</td>
      <td style="font-size:10px">${m.motivo_ajuste||m.paciente||'—'}</td>
      <td style="font-size:10px">${m.usuario_nombre||'—'}</td>
    </tr>`).join('');
    imprimirHTML(`
      <h2>Kardex — ${prodInfo?.nombre||''}</h2>
      <p style="color:#6c757d;margin-bottom:4px">${prodInfo?.codigo||''} · ${fmtFecha(desde)} al ${fmtFecha(hasta)}</p>
      <p style="margin-bottom:12px;font-size:11px">
        Entradas: <strong>${entradas}</strong> &nbsp;|&nbsp;
        Salidas: <strong>${salidas}</strong> &nbsp;|&nbsp;
        Ajustes: <strong>${ajustes}</strong>
      </p>
      <table>
        <thead><tr>
          <th>Fecha</th><th>Tipo</th><th>Lote</th><th>Cant.</th>
          <th>Costo</th><th>Ant.</th><th>Nuevo</th><th>Motivo/Paciente</th><th>Usuario</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
    `, `Kardex ${prodInfo?.nombre||''} ${desde} ${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Kardex por producto</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Producto</label>
            <select style={{ ...S.input, minWidth:'220px' }} value={selProd} onChange={e=>setSelProd(e.target.value)}>
              <option value="">— Seleccionar —</option>
              {productos.map(p => <option key={p.id} value={p.id}>{p.nombre} ({p.codigo||p.id})</option>)}
            </select>
          </div>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar} disabled={!selProd}>Generar</button>
          {movs.length>0 && <>
            <button style={S.btnVerde} onClick={()=>exportarCSV(movs,COLS_CSV,`Kardex_${prodInfo?.codigo||selProd}_${desde}_${hasta}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && movs.length===0 && <p style={{ color:C.gris,fontSize:'13px' }}>Sin movimientos en este rango.</p>}

      {!cargando && movs.length>0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Movimientos',  valor:movs.length, color:C.azul    },
              { label:'Entradas',     valor:entradas,    color:C.verde   },
              { label:'Salidas',      valor:salidas,     color:C.rojo    },
              { label:'Ajustes',      valor:ajustes,     color:C.naranja },
            ].map(k=>(
              <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'18px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Fecha','Tipo','Referencia','Lote','Vence lote','Cantidad','Costo unit.','Stock ant.','Stock nuevo','Motivo / Paciente','Usuario'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {movs.map((m,i)=>(
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td,whiteSpace:'nowrap',fontSize:'11px',color:C.gris }}>{fmtDT(m.fecha)}</td>
                    <td style={S.td}>
                      <span style={S.badge(COLOR_MOV[m.tipo_mov]||C.gris)}>{m.tipo_mov}</span>
                    </td>
                    <td style={{ ...S.td,fontSize:'11px',color:C.gris }}>{m.tipo_referencia||'—'}</td>
                    <td style={{ ...S.td,fontWeight:'600',color:C.azul,fontSize:'11px' }}>{m.numero_lote||'—'}</td>
                    <td style={{ ...S.td,fontSize:'11px' }}>{fmtFecha(m.lote_vencimiento)}</td>
                    <td style={{ ...S.td,fontWeight:'700',textAlign:'right',color:esPos(m)?C.verde:C.rojo }}>
                      {esPos(m)?'+':'−'}{m.cantidad}
                    </td>
                    <td style={{ ...S.td,textAlign:'right' }}>{m.costo_unitario!=null?fmtMonto(m.costo_unitario):'—'}</td>
                    <td style={{ ...S.td,textAlign:'right',color:C.gris }}>{m.stock_anterior}</td>
                    <td style={{ ...S.td,textAlign:'right',fontWeight:'700',color:C.azul }}>{m.stock_nuevo}</td>
                    <td style={{ ...S.td,fontSize:'11px' }}>{m.motivo_ajuste||m.paciente||'—'}</td>
                    <td style={{ ...S.td,fontSize:'11px',color:C.gris }}>{m.usuario_nombre||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize:'11px',color:C.gris,marginTop:'8px',textAlign:'right' }}>
            {movs.length} movimientos · {prodInfo?.nombre}
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  6. MEDICAMENTOS CONTROLADOS
// ════════════════════════════════════════════════════════════
function SeccionReporteControlados() {
  const [desde,    setDesde]    = useState(primerDiaMes());
  const [hasta,    setHasta]    = useState(hoy());
  const [datos,    setDatos]    = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscado,  setBuscado]  = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.controlados({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const totalUnidades = datos.reduce((s,d)=>s+Number(d.cantidad),0);
  const totalMonto    = datos.reduce((s,d)=>s+Number(d.total),0);
  const recetasUnicas = new Set(datos.map(d=>d.num_receta)).size;
  const medicsUnicos  = new Set(datos.map(d=>d.medicamento)).size;

  const COLS_CSV = [
    { key:'registrado_en',    label:'Fecha/Hora'        },
    { key:'medicamento',      label:'Medicamento'       },
    { key:'codigo',           label:'Código'            },
    { key:'cantidad',         label:'Cantidad'          },
    { key:'precio_unitario',  label:'Precio unit. ($)'  },
    { key:'total',            label:'Total ($)'         },
    { key:'num_receta',       label:'N° Receta'         },
    { key:'dr_prescriptor',   label:'Médico prescriptor'},
    { key:'numero_lote',      label:'Lote'              },
    { key:'lote_vencimiento', label:'Vence lote'        },
    { key:'paciente',         label:'Paciente'          },
    { key:'dui',              label:'DUI paciente'      },
    { key:'tipo_ingreso',     label:'Tipo ingreso'      },
    { key:'admision_id',      label:'N° Admisión'       },
    { key:'despachado_por',   label:'Despachado por'    },
  ];

  const imprimirPDF = () => {
    const filas = datos.map(d=>`<tr>
      <td style="white-space:nowrap;font-size:10px">${fmtDT(d.registrado_en)}</td>
      <td>${d.medicamento} <span class="badge-ctrl">CTRL</span></td>
      <td style="text-align:center">${d.cantidad}</td>
      <td style="font-weight:700;color:#6a1b9a">${d.num_receta}</td>
      <td>${d.dr_prescriptor}</td>
      <td>${d.numero_lote||'—'}</td>
      <td>${d.paciente}</td>
      <td>${d.dui||'—'}</td>
      <td>#${d.admision_id}</td>
      <td style="font-size:10px">${d.despachado_por}</td>
    </tr>`).join('');
    imprimirHTML(`
      <h2>Despacho de medicamentos controlados</h2>
      <p style="color:#6c757d;margin-bottom:4px">${fmtFecha(desde)} al ${fmtFecha(hasta)} · ${datos.length} despachos</p>
      <p style="margin-bottom:12px;font-size:11px">
        Recetas únicas: <strong>${recetasUnicas}</strong> &nbsp;|&nbsp;
        Medicamentos distintos: <strong>${medicsUnicos}</strong> &nbsp;|&nbsp;
        Total monto: <strong>${fmtMonto(totalMonto)}</strong>
      </p>
      <table>
        <thead><tr>
          <th>Fecha</th><th>Medicamento</th><th>Cant.</th>
          <th>N° Receta</th><th>Dr. Prescriptor</th><th>Lote</th>
          <th>Paciente</th><th>DUI</th><th>Admisión</th><th>Despachado por</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
      <div class="total-box">
        <span>Total despachos · Unidades: ${totalUnidades}</span>
        <span>${fmtMonto(totalMonto)}</span>
      </div>
    `, `Controlados ${desde} ${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={{ ...S.cardTit, borderLeft:`4px solid ${C.morado}`, paddingLeft:'12px' }}>
        <span style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <span style={S.badge(C.morado)}>CONTROLADOS</span>
          Registro de despachos
        </span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnMor} onClick={cargar}>Generar</button>
          {datos.length>0 && <>
            <button style={S.btnVerde} onClick={()=>exportarCSV(datos,COLS_CSV,`Controlados_${desde}_${hasta}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      <div style={{ background:'#f3e5f5', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'12px', color:'#6a1b9a' }}>
        ⚠️ Este reporte es de uso legal y administrativo. Cada despacho debe tener receta válida y prescriptor registrado.
        Los datos aquí son el respaldo formal del sistema.
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && datos.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin despachos de controlados en este período.</p>
      )}

      {!cargando && datos.length>0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Despachos',         valor:datos.length,    color:C.morado  },
              { label:'Recetas únicas',    valor:recetasUnicas,   color:C.azul    },
              { label:'Medicamentos dist.',valor:medicsUnicos,    color:C.naranja },
              { label:'Monto total',       valor:fmtMonto(totalMonto), color:C.verde   },
            ].map(k=>(
              <div key={k.label} style={{ background:'#f3e5f5', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:'#7b1fa2', marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Fecha','Medicamento','Cant.','N° Receta','Médico prescriptor','Lote','Vence','Paciente','DUI','Admisión','Despachado por'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((d,i)=>(
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#fce4ec'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td,whiteSpace:'nowrap',fontSize:'11px',color:C.gris }}>{fmtDT(d.registrado_en)}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight:'700',color:C.azul }}>{d.medicamento}</div>
                      <div style={{ fontSize:'10px',color:C.gris }}>{d.codigo}</div>
                    </td>
                    <td style={{ ...S.td,textAlign:'center',fontWeight:'700' }}>{d.cantidad}</td>
                    <td style={{ ...S.td,fontWeight:'700',color:C.morado,whiteSpace:'nowrap' }}>{d.num_receta}</td>
                    <td style={{ ...S.td,fontSize:'11px' }}>{d.dr_prescriptor}</td>
                    <td style={{ ...S.td,fontSize:'11px',fontWeight:'600',color:C.azul }}>{d.numero_lote||'—'}</td>
                    <td style={{ ...S.td,fontSize:'11px' }}>{fmtFecha(d.lote_vencimiento)}</td>
                    <td style={S.td}>{d.paciente}</td>
                    <td style={{ ...S.td,fontSize:'11px',color:C.gris }}>{d.dui||'—'}</td>
                    <td style={{ ...S.td,textAlign:'center' }}>#{d.admision_id}</td>
                    <td style={{ ...S.td,fontSize:'11px',color:C.gris }}>{d.despachado_por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={S.totalBox}>
            <span style={{ color:'#fff',fontSize:'13px' }}>
              {datos.length} despachos · {totalUnidades} unidades totales
            </span>
            <span style={{ color:'#fff',fontSize:'18px',fontWeight:'800' }}>{fmtMonto(totalMonto)}</span>
          </div>
        </>
      )}
    </div>
  );
}
